import AudioPlayer from './utils/audio-player';
import select from './utils/select';
import selectAll from './utils/select-all';
import SlideShow from './utils/slide-show';
import Swiper from './swiper';

import charles from '../assets/captions/charles.json';
import diana from '../assets/captions/diana.json';
import kathleen from '../assets/captions/kathleen.json';
import patrick from '../assets/captions/patrick.json';
import raymond from '../assets/captions/raymond.json';

const captions = { charles, diana, kathleen, patrick, raymond };

const audioControl = select('#audio-control');
const nextButton = select('#next-button');
const nextButtonIcon = select('.icon', nextButton);
const nextButtonText = select('.js-next-button-text', nextButton);
const prevButton = select('#prev-button');
const replayButton = select('#replay-button');

const slideTranscript = select('#slide-transcript');
const transcriptElement = select('span', slideTranscript);

// state
let currentCaption = null;
let captionTimeout = null;

// event tracking
const eventPercents = [10, 25, 50, 75, 90, 100];
const audioPercents = [25, 50, 75, 100];
const absoluteEventCache = {};
const percentEventCache = {};
let audioEventCache = {};

const audioSlides = selectAll('.swiper-slide[data-slide-slug]');
const player = new AudioPlayer({ template: 'assets/audio/{{slug}}.{{ext}}' });

function toggleAudioSvgs(state) {
  // first turn them all off
  selectAll('svg', audioControl).forEach(svg => svg.classList.add('hidden'));

  if (state === 'playing')
    audioControl.querySelector('.audio-playing').classList.remove('hidden');
  if (state === 'muted')
    audioControl.querySelector('.audio-muted').classList.remove('hidden');
}

const swiper = new Swiper('.swiper-container', {
  pagination: {
    el: '.swiper-pagination',
  },
  direction: 'vertical',
  speed: 600,
  slidesPerView: 1,
  mousewheel: true,
  keyboard: true,
  // Disable preloading of all images
  preloadImages: false,
  // Enable lazy loading
  lazy: {
    loadPrevNext: true,
  },

  fadeEffect: {
    crossFade: true,
  },

  navigation: {
    nextEl: '#next-button',
    prevEl: '#prev-button',
  },
});

swiper.on('slideChangeTransitionStart', function() {
  const instance = this;
  const activeIndex = instance.activeIndex;
  const activeSlide = instance.slides[activeIndex];

  const slug = activeSlide.getAttribute('data-slide-slug');

  if (slug) {
    currentCaption = captions[slug];
    const slideForegroundContent = select(
      '.slide-foreground-content',
      activeSlide
    );
    const slideMedia = select('.slide-media', activeSlide);
    slideForegroundContent.classList.remove('fade');
    slideMedia.classList.remove('fade');
  } else {
    currentCaption = null;
  }

  transcriptElement.textContent = '';
  audioControl.classList.add('hidden');
  player.stopAll();
});

swiper.on('slideChangeTransitionEnd', function() {
  const instance = this;
  const activeIndex = instance.activeIndex;
  const normalizedIndex = activeIndex + 1;
  const numSlides = instance.slides.length;
  const activeSlide = instance.slides[activeIndex];

  const slug = activeSlide.getAttribute('data-slide-slug');
  if (!slug) transcriptElement.textContent = '';

  if (activeIndex > 0 && !instance.isEnd) {
    nextButtonText.textContent = 'Next';
    nextButtonIcon.classList.remove('icon--animated');
    nextButton.classList.remove('hidden');
    prevButton.classList.remove('hidden');
    replayButton.classList.add('hidden');
  }

  // events
  if (!absoluteEventCache[normalizedIndex]) {
    absoluteEventCache[normalizedIndex] = true;

    ga('send', {
      hitType: 'event',
      eventCategory: 'Data Visuals - Slider',
      eventAction: 'Absolute - Slide Depth',
      eventLabel: `${normalizedIndex}/${numSlides}`,
      eventValue: normalizedIndex,
    });
  }

  const percentComplete = normalizedIndex / numSlides * 100;

  eventPercents.forEach(p => {
    if (percentComplete >= p && !percentEventCache[p]) {
      percentEventCache[p] = true;

      ga('send', {
        hitType: 'event',
        eventCategory: 'Data Visuals - Slider',
        eventAction: 'Percent - Slide Depth',
        eventLabel: `${p}%`,
        eventValue: p,
      });
    }
  });
});

swiper.on('reachBeginning', function() {
  player.stopAll();
  nextButtonText.textContent = 'Begin';
  nextButtonIcon.classList.add('icon--animated');
  nextButton.classList.remove('hidden');
  prevButton.classList.add('hidden');
});

swiper.on('reachEnd', function() {
  player.stopAll();
  nextButton.classList.add('hidden');
  replayButton.classList.remove('hidden');
});

audioSlides.forEach(slide => {
  const slug = slide.getAttribute('data-slide-slug');
  const minutes = slide.getAttribute('data-slide-minutes');
  const seconds = slide.getAttribute('data-slide-seconds');
  const durationInSeconds = parseInt(minutes, 10) * 60 + parseInt(seconds, 10);

  const playButton = select('.js-play-audio', slide);
  const slideMedia = select('.slide-media', slide);
  const slideMediaAudio = select('.slide-media-audio', slide);
  const slideForegroundContent = select('.slide-foreground-content', slide);

  const sound = player.addSound(slug);

  playButton.addEventListener('click', () => {
    audioEventCache = {};
    slideForegroundContent.classList.add('fade');
    slideMedia.classList.add('fade');
    player.play(slug);
    audioControl.classList.remove('hidden');

    ga('send', {
      hitType: 'event',
      eventCategory: 'Data Visuals - Audio',
      eventAction: 'play',
      eventLabel: slug,
      eventValue: 0,
    });

    const onEndSound = () => {
      ga('send', {
        hitType: 'event',
        eventCategory: 'Data Visuals - Audio',
        eventAction: 'complete',
        eventLabel: slug,
        eventValue: 100,
      });

      setTimeout(() => {
        transcriptElement.textContent = '';
        slideForegroundContent.classList.remove('fade');
        slideMedia.classList.remove('fade');
        audioControl.classList.add('hidden');
      }, 2000);
      sound.removeEventListener('ended', onEndSound, false);
    };

    sound.addEventListener('ended', onEndSound, false);
  });
});

player.on('mute', () => {
  toggleAudioSvgs('muted');

  ga('send', {
    hitType: 'event',
    eventCategory: 'Data Visuals - Audio',
    eventAction: 'mute',
  });
});

player.on('unmute', () => {
  toggleAudioSvgs('playing');

  ga('send', {
    hitType: 'event',
    eventCategory: 'Data Visuals - Audio',
    eventAction: 'unmute',
  });
});

audioControl.addEventListener('click', () => {
  if (player.isMuted) {
    player.unmute();
  } else {
    player.mute();
  }
});

player.on('tick', ({ duration, position, slug }) => {
  let activeText = '';

  for (let i = 0; i < currentCaption.length; i++) {
    const entry = currentCaption[i];

    if (position < entry.start) {
      if (i > 0) {
        activeText = currentCaption[i - 1].text;
      } else {
        activeText = entry.text;
      }
      break;
    } else {
      activeText = entry.text;
    }
  }

  if (activeText.length !== 0) {
    transcriptElement.textContent = activeText;
  }

  const percentAudioComplete = Math.round(position / duration * 100);

  audioPercents.forEach(p => {
    if (percentAudioComplete >= p && !audioEventCache[p]) {
      audioEventCache[p] = true;

      ga('send', {
        hitType: 'event',
        eventCategory: 'Data Visuals - Audio',
        eventAction: 'progress',
        eventLabel: slug,
        eventValue: p,
      });
    }
  });
});

replayButton.addEventListener('click', () => {
  nextButtonText.textContent = 'Begin';
  nextButton.classList.remove('hidden');
  replayButton.classList.add('hidden');

  swiper.slideTo(0, 0);
});

export default () => {};



// WEBPACK FOOTER //
// ./main.js